import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { forwardRef, isValidElement, Children, useState, useMemo } from 'react';
import { useId, AndesContext, getPatchedI18n } from '@andes/context';
import classNames from 'classnames';
import _jsx from '@babel/runtime/helpers/jsx';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import ChevronDown24 from '@andes/icons/ChevronDown24';
import { Checkbox } from '@andes/checkbox';
import ArrowDown16 from '@andes/icons/ArrowDown16';
import Sort16 from '@andes/icons/Sort16';
import Edit16 from '@andes/icons/Edit16';
var selectableTypes = {
  ALL: 'ALL',
  SOME: 'SOME',
  NONE: 'NONE',
  DISABLED: 'DISABLED'
};
const namespace$2 = 'andes-table';
class Table extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selection: selectableTypes.NONE,
      sortBy: props.sortBy || '',
      sort: props.sort || 'desc'
    };
    this.onRowSelection = this.onRowSelection.bind(this);
    this.onHeadSelection = this.onHeadSelection.bind(this);
    this.renderChildren = this.renderChildren.bind(this);
    this.getChildProps = this.getChildProps.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
  }
  onHeadSelection(checked) {
    this.setState({
      selection: checked ? selectableTypes.ALL : selectableTypes.NONE
    });
  }
  onRowSelection(selection) {
    this.setState({
      selection
    });
  }
  handleSortChange() {
    let sortBy = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    let sort = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'desc';
    const {
      onSortChange
    } = this.props;
    const previousValues = this.state;
    if (sort !== previousValues.sort || sortBy !== previousValues.sortBy) {
      this.setState({
        sort,
        sortBy
      });
      if (typeof onSortChange === 'function') onSortChange(sortBy, sort);
    }
  }
  renderChildren() {
    const {
      children
    } = this.props;
    return React.Children.map(children, child => {
      let renderElement = null;
      if (child !== null && child !== void 0 && child.type && (child.type.displayName === 'TableHead' || child.type.displayName === 'TableBody')) {
        renderElement = /*#__PURE__*/React.cloneElement(child, this.getChildProps(child.type.displayName, child.props));
      }
      return renderElement;
    });
  }
  getChildProps(displayName) {
    const {
      id,
      selectable = false
    } = this.props;
    const {
      selection,
      sortBy,
      sort
    } = this.state;
    const childProps = {
      selectable,
      selection,
      id: displayName === 'TableHead' ? "".concat(id, "-head") : "".concat(id, "-body")
    };
    if (displayName === 'TableHead') {
      childProps.onHeadSelection = this.onHeadSelection;
      childProps.sortBy = sortBy;
      childProps.sort = sort;
      childProps.handleSortChange = this.handleSortChange;
    }
    if (displayName === 'TableBody') {
      childProps.onRowSelection = this.onRowSelection;
    }
    return childProps;
  }
  render() {
    const {
      className,
      compact = false,
      innerRef
    } = this.props;
    const tableClassNames = classNames(namespace$2, className, {
      ["".concat(namespace$2, "--compact")]: compact
    });
    return /*#__PURE__*/React.createElement("table", {
      className: tableClassNames,
      ref: innerRef
    }, this.renderChildren());
  }
}
var Table$1 = Table;
const _excluded$7 = ["id"];
const TableContainer = /*#__PURE__*/function () {
  const TableContainer = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
        id
      } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded$7);
    const componentId = useId(id);
    return /*#__PURE__*/React.createElement(Table$1, _extends({}, otherProps, {
      id: componentId,
      innerRef: ref
    }));
  });
  TableContainer.displayName = 'TableContainer';
  return TableContainer;
}();
var TableContainer$1 = TableContainer;
class TableBody extends React.Component {
  constructor(props) {
    super(props);
    this.onSelectRow = this.onSelectRow.bind(this);
    this.getSelectionType = this.getSelectionType.bind(this);
    this.renderRows = this.renderRows.bind(this);
    this.selectedRows = [];
    this.disabledRows = [];
    this.allRows = 0;
    this.updateRowStates(props);
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      selection: currentSelection = selectableTypes.NONE,
      children
    } = this.props;
    if (currentSelection !== nextProps.selection) {
      if (nextProps.selection === selectableTypes.ALL || nextProps.selection === selectableTypes.NONE) {
        this.selectedRows = nextProps.children ? Array(React.Children.count(nextProps.children)).fill(nextProps.selection === selectableTypes.ALL) : [];
      }
    }
    if (children !== nextProps.children) {
      this.updateRowStates(nextProps);
    }
  }
  componentDidMount() {
    const {
      onRowSelection
    } = this.props;
    if (typeof onRowSelection === 'function') onRowSelection(this.getSelectionType());
  }
  onSelectRow(index, checked) {
    const {
      onRowSelection
    } = this.props;
    this.selectedRows[Number(index)] = checked;
    if (typeof onRowSelection === 'function') onRowSelection(this.getSelectionType());
  }
  updateRowStates(props) {
    this.allRows = React.Children.count(props.children);
    const {
      selectedRows,
      disabledRows
    } = React.Children.toArray(props.children).reduce((acc, child) => {
      if (/*#__PURE__*/React.isValidElement(child)) {
        const {
          selected,
          disabled
        } = child.props;
        acc.selectedRows.push(selected || false);
        acc.disabledRows.push(disabled || false);
      }
      return acc;
    }, {
      selectedRows: [],
      disabledRows: []
    });
    this.selectedRows = selectedRows;
    this.disabledRows = disabledRows;
  }
  getSelectionType() {
    var _selectionTypes$find;
    const allRowsSelected = this.selectedRows.filter(Boolean).length === this.allRows && this.disabledRows.filter(Boolean).length === 0;
    const selectionTypes = [{
      condition: () => this.disabledRows.every(Boolean),
      type: selectableTypes.DISABLED
    }, {
      condition: () => this.disabledRows.every((value, index) => value === this.selectedRows[index]),
      type: selectableTypes.NONE
    }, {
      condition: () => this.selectedRows.filter(Boolean).length > 0 && !allRowsSelected,
      type: selectableTypes.SOME
    }, {
      condition: () => this.selectedRows.filter(Boolean).length === this.allRows,
      type: selectableTypes.ALL
    }];
    const selectionType = ((_selectionTypes$find = selectionTypes.find(_ref => {
      let {
        condition
      } = _ref;
      return condition();
    })) === null || _selectionTypes$find === void 0 ? void 0 : _selectionTypes$find.type) || selectableTypes.NONE;
    return selectionType;
  }
  renderRows() {
    const {
      children,
      selectable = false,
      id
    } = this.props;
    return React.Children.map(children, (child, index) => {
      let rowElement = null;
      if (/*#__PURE__*/isValidElement(child) && child.type && child.type.displayName === 'TableRow') {
        const childProps = {
          selectable,
          onSelectRow: this.onSelectRow,
          selected: this.selectedRows[Number(index)],
          disabled: this.disabledRows[Number(index)],
          index,
          id: "".concat(id, "-row-").concat(index)
        };
        rowElement = /*#__PURE__*/React.cloneElement(child, childProps);
      }
      return rowElement;
    });
  }
  render() {
    const {
      innerRef
    } = this.props;
    return /*#__PURE__*/React.createElement("tbody", {
      className: "".concat(namespace$2, "__body"),
      ref: innerRef
    }, this.renderRows());
  }
}
TableBody.displayName = 'TableBody';
var TableBody$1 = TableBody;
const _excluded$6 = ["id"];
const TableBodyContainer = /*#__PURE__*/function () {
  const TableBodyContainer = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
        id
      } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded$6);
    const componentId = useId(id);
    return /*#__PURE__*/React.createElement(TableBody$1, _extends({
      id: componentId
    }, otherProps, {
      innerRef: ref
    }));
  });
  TableBodyContainer.displayName = 'TableBody';
  return TableBodyContainer;
}();
var TableBodyContainer$1 = TableBodyContainer;
var _ChevronDown;
const _excluded$5 = ["className", "children", "align", "verticalAlign", "textContentMaxLines", "expandable", "innerRef", "id", "expandableDataContentProps"];
class TableData extends React.PureComponent {
  render() {
    const _this$props = this.props,
      {
        className,
        children,
        align = 'left',
        verticalAlign = 'center',
        textContentMaxLines,
        expandable,
        innerRef,
        id,
        expandableDataContentProps
      } = _this$props,
      other = _objectWithoutProperties(_this$props, _excluded$5);
    const isExpandable = expandable !== undefined;
    const isExpandableOpened = isExpandable && expandable.open;
    const onExpandableClick = isExpandable ? expandable.onClick : undefined;
    const expandableLabel = isExpandable ? expandable.ariaLabel : undefined;
    const tableDataClassnames = classNames("".concat(namespace$2, "__column"), "".concat(namespace$2, "__column--").concat(align), "".concat(namespace$2, "__column--vertical-align-").concat(verticalAlign), isExpandable && "".concat(namespace$2, "__expandable"), isExpandable && "".concat(namespace$2, "__expandable-").concat(isExpandableOpened ? 'open' : 'close'), className);
    const tableDataHTMLProps = _objectSpread({
      id
    }, other);
    const childrenArray = Children.toArray(children);
    const childIsString = childrenArray.length === 1 && typeof childrenArray[0] === 'string';
    const lineClampValue = textContentMaxLines !== null && textContentMaxLines !== void 0 ? textContentMaxLines : 'none';
    const tableDataContentStyle = childIsString ? {
      lineClamp: lineClampValue,
      WebkitLineClamp: lineClampValue
    } : undefined;
    const tableDataContent = childIsString ? /*#__PURE__*/React.createElement("span", _extends({
      id: "".concat(id, "-value")
    }, expandableDataContentProps, {
      className: "".concat(namespace$2, "__column--value"),
      style: tableDataContentStyle
    }), children) : /*#__PURE__*/React.createElement("div", _extends({
      id: "".concat(id, "-value")
    }, expandableDataContentProps), children);
    return /*#__PURE__*/React.createElement("td", _extends({
      className: tableDataClassnames
    }, tableDataHTMLProps, {
      ref: innerRef
    }), isExpandable ? /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace$2, "__expandable--container")
    }, void 0, /*#__PURE__*/_jsx("button", {
      className: "".concat(namespace$2, "__expandable--trigger"),
      onClick: onExpandableClick,
      "aria-label": expandableLabel,
      "aria-labelledby": "".concat(id, "-expandable-trigger ").concat(id, "-value"),
      id: "".concat(id, "-expandable-trigger"),
      "aria-expanded": expandable.open,
      type: "button"
    }, void 0, _ChevronDown || (_ChevronDown = /*#__PURE__*/_jsx(ChevronDown24, {}))), tableDataContent) : tableDataContent);
  }
}
TableData.displayName = 'TableData';
var TableData$1 = TableData;
const _excluded$4 = ["id"];
const TableDataContainer = /*#__PURE__*/function () {
  const TableDataContainer = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
        id
      } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded$4);
    const componentId = useId(id);
    return /*#__PURE__*/React.createElement(TableData$1, _extends({
      id: componentId
    }, otherProps, {
      innerRef: ref
    }));
  });
  TableDataContainer.displayName = 'TableData';
  return TableDataContainer;
}();
var TableDataContainer$1 = TableDataContainer;
var module$2 = "@andes/table";
var locale$2 = "en-US";
var translations$2 = {
  "andes-table\u0004Seleccionar fila": [null, "Select row"],
  "andes-table\u0004Seleccionar todas las filas": [null, "Select all rows"]
};
var enTranslations = {
  module: module$2,
  locale: locale$2,
  translations: translations$2
};
var module$1 = "@andes/table";
var locale$1 = "es-AR";
var translations$1 = {
  "andes-table\u0004Seleccionar fila": [null, "Seleccionar fila"],
  "andes-table\u0004Seleccionar todas las filas": [null, "Seleccionar todas las filas"]
};
var esTranslations = {
  module: module$1,
  locale: locale$1,
  translations: translations$1
};
var module = "@andes/table";
var locale = "pt-BR";
var translations = {
  "andes-table\u0004Seleccionar fila": [null, "Selecionar linha"],
  "andes-table\u0004Seleccionar todas las filas": [null, "Selecionar todas as linhas"]
};
var ptTranslations = {
  module: module,
  locale: locale,
  translations: translations
};
class TableHead extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selection: props.selection || selectableTypes.NONE
    };
    this.selectableHandler = this.selectableHandler.bind(this);
    this.renderChildren = this.renderChildren.bind(this);
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      selection: nextProps.selection
    });
  }
  selectableHandler(event) {
    const {
      onHeadSelection
    } = this.props;
    const {
      checked
    } = event.target;
    this.setState({
      selection: checked ? selectableTypes.ALL : selectableTypes.NONE
    });
    if (typeof onHeadSelection === 'function') {
      onHeadSelection(checked);
    }
  }
  renderChildren() {
    const {
      children,
      id,
      sortBy = '',
      sort = 'desc',
      handleSortChange
    } = this.props;
    return React.Children.map(children, child => {
      var _child$type;
      const childDisplayName = child === null || child === void 0 || (_child$type = child.type) === null || _child$type === void 0 ? void 0 : _child$type.displayName;
      if (childDisplayName === 'TableHeaderContainer' || childDisplayName === 'TableHeader') {
        return /*#__PURE__*/React.cloneElement(child, {
          id,
          sortBy,
          sort: sortBy === child.props.sortId ? sort : 'asc',
          handleSortChange
        });
      }
      return null;
    });
  }
  render() {
    const {
      selectable = false,
      sticky = false,
      innerRef,
      id
    } = this.props;
    const {
      selection
    } = this.state;
    const TableHeadClassnames = classNames("".concat(namespace$2, "__head"), {
      ["".concat(namespace$2, "__head--sticky")]: sticky
    });
    return /*#__PURE__*/_jsx(AndesContext.Consumer, {}, void 0, andesContext => {
      const i18n = getPatchedI18n(andesContext, enTranslations, esTranslations, ptTranslations);
      return /*#__PURE__*/React.createElement("thead", {
        className: TableHeadClassnames,
        ref: innerRef
      }, /*#__PURE__*/_jsx("tr", {
        className: "".concat(namespace$2, "__row")
      }, void 0, selectable && /*#__PURE__*/
      _jsx("th", {
        className: "".concat(namespace$2, "__header ").concat(namespace$2, "__header-checkbox")
      }, void 0, /*#__PURE__*/_jsx(Checkbox, {
        checked: selection === selectableTypes.ALL || selection === selectableTypes.SOME,
        indeterminate: selection === selectableTypes.SOME,
        disabled: selection === selectableTypes.DISABLED,
        onChange: this.selectableHandler,
        srLabel: i18n.pgettext(namespace$2, 'Seleccionar todas las filas'),
        id: "".concat(id, "-head-selectable")
      })), this.renderChildren()));
    });
  }
}
TableHead.displayName = 'TableHead';
var TableHead$1 = TableHead;
const _excluded$3 = ["id"];
const TableHeadContainer = /*#__PURE__*/function () {
  const TableHeadContainer = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
        id
      } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded$3);
    const componentId = useId(id);
    return /*#__PURE__*/React.createElement(TableHead$1, _extends({
      id: componentId
    }, otherProps, {
      innerRef: ref
    }));
  });
  TableHeadContainer.displayName = 'TableHead';
  return TableHeadContainer;
}();
var TableHeadContainer$1 = TableHeadContainer;
var _ArrowDown, _Sort;
const _excluded$2 = ["className", "children", "align", "sort", "sortBy", "sortId", "handleSortChange", "id", "innerRef"];
const namespace$1 = "".concat(namespace$2, "__header");
class TableHeader extends React.PureComponent {
  constructor(props) {
    super(props);
    this.handleSort = this.handleSort.bind(this);
  }
  handleSort() {
    const {
      sort = 'asc',
      sortId,
      handleSortChange
    } = this.props;
    const newSort = sort === 'asc' ? 'desc' : 'asc';
    if (typeof handleSortChange === 'function') handleSortChange(sortId, newSort);
  }
  render() {
    const _this$props = this.props,
      {
        className,
        children,
        align = 'left',
        sort = 'asc',
        sortBy = '',
        sortId,
        handleSortChange,
        id,
        innerRef
      } = _this$props,
      other = _objectWithoutProperties(_this$props, _excluded$2);
    const isHeaderSorted = sortId === sortBy;
    const tableHeaderClassnames = classNames(namespace$1, "".concat(namespace$1, "--").concat(align), className, {
      ["".concat(namespace$1, "--sortable")]: sortId,
      ["".concat(namespace$1, "--sorted-").concat(sort)]: sort && isHeaderSorted
    });
    const iconContainerClassnames = classNames("".concat(namespace$2, "__sort-icon-container"), {
      ["".concat(namespace$2, "__sort-icon-container--sorted")]: isHeaderSorted
    });
    const headerId = sortId ? "".concat(id, "-header-").concat(sortId) : undefined;
    const ariaProps = {};
    if (sortId) {
      ariaProps.onClick = this.handleSort;
      ariaProps['aria-controls'] = headerId;
      if (isHeaderSorted) {
        ariaProps['aria-sort'] = sort === 'asc' ? 'ascending' : 'descending';
      }
    }
    const Container = sortId ? 'button' : 'div';
    return /*#__PURE__*/React.createElement("th", _extends({
      className: tableHeaderClassnames,
      scope: "col",
      id: headerId
    }, other, {
      ref: innerRef
    }), /*#__PURE__*/React.createElement(Container, _extends({
      className: "".concat(namespace$1, "__container")
    }, ariaProps, {
      type: sortId ? 'button' : undefined
    }), children, sortId && /*#__PURE__*/_jsx("div", {
      className: iconContainerClassnames,
      "aria-hidden": true
    }, void 0, isHeaderSorted ? _ArrowDown || (_ArrowDown = /*#__PURE__*/_jsx(ArrowDown16, {})) : _Sort || (_Sort = /*#__PURE__*/_jsx(Sort16, {})))));
  }
}
TableHeader.displayName = 'TableHeader';
var TableHeader$1 = TableHeader;
const _excluded$1 = ["id"];
const TableHeaderContainer = /*#__PURE__*/function () {
  const TableHeaderContainer = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
        id
      } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded$1);
    const componentId = useId(id);
    return /*#__PURE__*/React.createElement(TableHeader$1, _extends({
      id: componentId
    }, otherProps, {
      innerRef: ref
    }));
  });
  TableHeaderContainer.displayName = 'TableHeader';
  return TableHeaderContainer;
}();
var TableHeaderContainer$1 = TableHeaderContainer;
class TableRow extends React.Component {
  constructor(props) {
    super(props);
    const {
      selected = false,
      defaultExpanded = false,
      disabled = false
    } = props;
    this.state = {
      selected,
      disabled,
      expandableOpen: this.isExpandableControlled || defaultExpanded
    };
    this.clickHandler = this.clickHandler.bind(this);
    this.getExpandableOpen = this.getExpandableOpen.bind(this);
    this.expandableClickHandler = this.expandableClickHandler.bind(this);
    this.renderExpandableRowColumns = this.renderExpandableRowColumns.bind(this);
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      selected = false,
      onCheckRow,
      onSelectRow,
      index = 0
    } = this.props;
    if (selected !== nextProps.selected) {
      this.setState({
        selected: nextProps.selected || false
      });
      if (typeof onCheckRow === 'function') {
        onCheckRow(index, nextProps.selected);
      }
      if (typeof onSelectRow === 'function') {
        onSelectRow(index, !!nextProps.selected);
      }
    }
  }
  get isExpandable() {
    const {
      expandable
    } = this.props;
    return expandable !== undefined;
  }
  get isExpandableControlled() {
    const {
      expanded
    } = this.props;
    return this.isExpandable && expanded !== undefined;
  }
  getExpandableOpen() {
    const {
      expanded
    } = this.props;
    const {
      expandableOpen
    } = this.state;
    if (this.isExpandableControlled) return expanded;
    return expandableOpen;
  }
  clickHandler(event) {
    const {
      onSelectRow,
      index = 0
    } = this.props;
    const {
      checked
    } = event.target;
    this.setState({
      selected: checked
    });
    if (typeof onSelectRow === 'function') {
      onSelectRow(index, checked);
    }
  }
  expandableClickHandler(event) {
    const {
      onOpen,
      onClose
    } = this.props;
    const {
      expandableOpen
    } = this.state;
    if (!this.isExpandableControlled) {
      this.setState({
        expandableOpen: !expandableOpen
      });
    }
    if (this.getExpandableOpen() && typeof onClose === 'function') {
      onClose(event);
    } else if (!this.getExpandableOpen() && typeof onOpen === 'function') {
      onOpen(event);
    }
  }
  renderExpandableRowColumns() {
    const {
      children,
      expandable,
      id
    } = this.props;
    const {
      buttonLabel
    } = expandable || {};
    return Children.toArray(children).filter(child => /*#__PURE__*/isValidElement(child) && child.type && child.type.displayName === 'TableData').map((child, index) => {
      const tableCellId = "".concat(id, "-").concat(index, "-expandable-row");
      const childProps = {
        id: tableCellId,
        expandable: index === 0 ? {
          open: this.getExpandableOpen(),
          onClick: this.expandableClickHandler,
          ariaLabel: buttonLabel
        } : undefined
      };
      return /*#__PURE__*/React.cloneElement(child, childProps);
    });
  }
  renderExpandableContent() {
    const {
      expandable,
      selectable = false,
      children,
      id
    } = this.props;
    const {
      type = 'content',
      content
    } = expandable || {};
    const childrenNumber = Children.toArray(children).reduce((colsNumber, child) => {
      var _child$props$colSpan;
      if (/*#__PURE__*/isValidElement(child)) return colsNumber + ((_child$props$colSpan = child.props.colSpan) !== null && _child$props$colSpan !== void 0 ? _child$props$colSpan : 1);
      return 0;
    }, 0);
    const numberOfColumns = Number(childrenNumber) + (selectable ? 1 : 0);
    const a11yProps = {
      role: 'region',
      'aria-labelledby': "".concat(id, "-0-expandable-row")
    };
    return type === 'content' ? /*#__PURE__*/_jsx(TableRow, {
      id: "".concat(id, "-expandable-row")
    }, void 0, /*#__PURE__*/_jsx(TableData$1, {
      id: "".concat(id, "-expandable-data"),
      colSpan: numberOfColumns,
      expandableDataContentProps: a11yProps
    }, void 0, content)) : content;
  }
  render() {
    const {
      className,
      selectable = false,
      children,
      innerRef,
      id
    } = this.props;
    const {
      selected,
      disabled
    } = this.state;
    const canBeSelected = selected && !disabled;
    const tableRowClassnames = classNames("".concat(namespace$2, "__row"), this.isExpandable && "".concat(namespace$2, "__expandable--row"), className);
    return /*#__PURE__*/_jsx(AndesContext.Consumer, {}, void 0, andesContext => {
      const i18n = getPatchedI18n(andesContext, enTranslations, esTranslations, ptTranslations);
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
        className: tableRowClassnames,
        ref: innerRef
      }, selectable && /*#__PURE__*/
      _jsx("td", {
        className: "".concat(namespace$2, "__column ").concat(namespace$2, "__column-checkbox")
      }, void 0, /*#__PURE__*/_jsx(Checkbox, {
        disabled: disabled,
        checked: canBeSelected,
        onChange: this.clickHandler,
        srLabel: i18n.pgettext(namespace$2, 'Seleccionar fila'),
        id: "".concat(id, "-row-selectable")
      })), this.isExpandable ? this.renderExpandableRowColumns() : children), this.isExpandable && this.getExpandableOpen() ? this.renderExpandableContent() : null);
    });
  }
}
TableRow.displayName = 'TableRow';
var TableRow$1 = TableRow;
const _excluded = ["id"];
const TableRowContainer = /*#__PURE__*/function () {
  const TableRowContainer = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
        id
      } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);
    const componentId = useId(id);
    return /*#__PURE__*/React.createElement(TableRow$1, _extends({
      id: componentId
    }, otherProps, {
      innerRef: ref
    }));
  });
  TableRowContainer.displayName = 'TableRow';
  return TableRowContainer;
}();
var TableRowContainer$1 = TableRowContainer;
var _div, _svg, _svg2, _svg3;
const tableNamespace = 'andes-table';
const namespace = "".concat(tableNamespace, "__editable");
const EditableTableData = /*#__PURE__*/function () {
  const EditableTableData = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
      className,
      srLabel,
      defaultValue = '',
      value,
      onChange,
      disabled = false,
      tableDataProps = {},
      inputProps = {},
      'data-title': dataTitle
    } = _ref;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const [focus, setFocus] = useState(false);
    const isControlled = useMemo(() => value !== undefined, []);
    const getInputValue = () => isControlled ? value : uncontrolledValue;
    const getInputState = () => {
      if (disabled) {
        return 'disabled';
      }
      if (focus) {
        return 'focused';
      }
      return 'enabled';
    };
    const tableDataClassnames = classNames("".concat(tableNamespace, "__column"), "".concat(namespace), "".concat(namespace, "-").concat(getInputState()), className);
    const handleChange = event => {
      const input = event.target;
      if (typeof onChange === 'function') onChange(event);
      if (!isControlled) setUncontrolledValue(input.value);
    };
    const handleFocus = event => {
      const {
        onFocus
      } = inputProps;
      setFocus(true);
      if (typeof onFocus === 'function') onFocus(event);
    };
    const handleBlur = event => {
      const {
        onBlur
      } = inputProps;
      setFocus(false);
      if (typeof onBlur === 'function') onBlur(event);
    };
    return /*#__PURE__*/React.createElement("td", _extends({}, tableDataProps, {
      className: tableDataClassnames,
      title: getInputValue(),
      "data-title": dataTitle,
      ref: ref
    }), /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace, "--input-container")
    }, void 0, /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
      className: "".concat(namespace, "--input"),
      value: value,
      defaultValue: defaultValue,
      "aria-label": srLabel,
      disabled: disabled,
      onChange: handleChange,
      onBlur: handleBlur,
      onFocus: handleFocus
    })), _div || (_div = /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace, "--input-icon"),
      "aria-hidden": true
    }, void 0, /*#__PURE__*/_jsx(Edit16, {
      color: "rgba(0, 0, 0, 0.25)"
    })))), _svg || (_svg = /*#__PURE__*/_jsx("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      className: "".concat(namespace, "--dashed-border"),
      "aria-hidden": true
    }, void 0, /*#__PURE__*/_jsx("rect", {
      width: "100%",
      height: "100%",
      fill: "none",
      rx: "2",
      ry: "2",
      strokeWidth: "2",
      strokeDasharray: "6, 2",
      strokeDashoffset: "0"
    }))), _svg2 || (_svg2 = /*#__PURE__*/_jsx("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      className: "".concat(namespace, "--mouseover-border"),
      "aria-hidden": true
    }, void 0, /*#__PURE__*/_jsx("rect", {
      width: "100%",
      height: "100%",
      fill: "none",
      rx: "0",
      ry: "0",
      strokeWidth: "2"
    }))), _svg3 || (_svg3 = /*#__PURE__*/_jsx("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      className: "".concat(namespace, "--focus-border"),
      "aria-hidden": true
    }, void 0, /*#__PURE__*/_jsx("rect", {
      width: "100%",
      height: "100%",
      fill: "none",
      rx: "2",
      ry: "2",
      strokeWidth: "4"
    }))));
  });
  EditableTableData.displayName = 'EditableTableData';
  return EditableTableData;
}();
var EditableTableData$1 = EditableTableData;
export { EditableTableData$1 as EditableTableData, TableContainer$1 as Table, TableBodyContainer$1 as TableBody, TableDataContainer$1 as TableData, TableHeadContainer$1 as TableHead, TableHeaderContainer$1 as TableHeader, TableRowContainer$1 as TableRow };
